import { Injectable } from '@angular/core'
import { EMPTY, Observable, catchError, map, switchMap, take, throwError } from 'rxjs'
import { Login } from 'src/app/models/model'
import { WebService } from '../api/web.service'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { environment } from 'src/environments/environment'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { getSelectedCompaniesFromState, getUserFromState } from 'src/app/store/selector/initialize.selector'
import { LocalStorageService } from '../common/local-storage.service'
import { CommonService } from '../common/common.service'
import { AppState } from 'src/app/store/state/app.state'
import { Store } from '@ngrx/store'
import {
  start_forgot_password,
  start_reset_password_with_password,
  start_select_client
} from 'src/app/store/action/auth.action'
import { HttpHandler, HttpHeaders, HttpRequest } from '@angular/common/http'
import { InitializeService } from '../initialize/initialize.service'
import {
  HttpErrorResponseModel,
  LoginDiscoveryResponseApiModel,
  SelectedCompanyAPIModel,
  TokenAPIModel
} from 'src/app/models/api.model'
import { LoadingService } from '../common/loading.service'

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static token_prefix = environment.token_prefix
  private static readonly TOKEN_KEY = AuthService.token_prefix + 'auth-token'
  private static readonly REFRESH_TOKEN_KEY = 'refresh'
  constructor(
    private cs: CommonService,
    private webService: WebService,
    private appState: Store<AppState>,
    private initializeService: InitializeService,
    private loadingService: LoadingService
  ) {}

  login(email: string, password: string): Observable<Login> {
    const formData = new FormData()
    formData.append('email', email)
    formData.append('password', password)

    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_LOGIN, formData, null, [], true)
      .pipe(map((data: Login) => data))
  }

  refreshToken = (request: HttpRequest<any>, next: HttpHandler) => {
    this.appState
      .select(getSelectedCompaniesFromState)
      .pipe(take(1))
      .subscribe(selectedCompanies => {
        if (!selectedCompanies) {
          this.clearLocalStorageAndRedirectToLogin()
          setTimeout(() => {
            this.loadingService.hide()
          }, 0)
          return throwError(() => 'Session expired')
        }
        const entityIds = this.getCompanyEnityIds(selectedCompanies)
        const companyIds = this.getCompanyGroupIds(selectedCompanies)
        let headers = this.createHeaders(entityIds, companyIds)
        return this.webService
          .getWithRefresh(ServiceUrlConstants.API_SESSION_REFRESH, null, headers)
          .pipe(
            switchMap((refreshTokenResponse: any) => {
              AuthService.setToken(refreshTokenResponse.token)
              AuthService.setRefreshToken(refreshTokenResponse.refreshToken)
              return next.handle(
                request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${refreshTokenResponse.token}`
                  }
                })
              )
            }),
            catchError(error => {
              if (JSON.parse(error).status === 401) {
                this.clearLocalStorageAndRedirectToLogin()
              }
              return throwError(() => {
                new Error('Something went wrong')
              })
            })
          )
          .subscribe()
      })
  }

  addRefreshHeader = (headers: HttpHeaders) => {
    if (!headers) {
      headers = new HttpHeaders()
    }
    const token = AuthService.getRefreshToken()
    headers = headers.set('refreshToken', `${token}`)
    return headers
  }

  logout = () => {
    const headers = new HttpHeaders()
    return this.webService.postWithAuthTokenAndRefresh(ServiceUrlConstants.API_LOGOUT, null, headers)
  }

  private createHeaders(entityIds: string, companyIds: string) {
    let headers = new HttpHeaders()
    headers = this.webService.addHeader(headers, 'x-companyEntityIds', entityIds)
    if (companyIds) {
      headers = this.webService.addHeader(headers, 'x-companyGroupId', companyIds)
    }
    return headers
  }

  private getCompanyGroupIds(selectedCompanies: SelectedCompanyAPIModel[]) {
    return selectedCompanies
      ?.filter(company => company.type === GlobalConstants.COMPANY_TYPE.GROUP)
      ?.map(company => company.id)
      .join(',')
  }

  private getCompanyEnityIds(selectedCompanies: SelectedCompanyAPIModel[]) {
    return selectedCompanies
      ?.filter(company => company.type === GlobalConstants.COMPANY_TYPE.ENTITY)
      ?.map(company => company.id)
      .join(',')
  }

  public static getToken(): string {
    return LocalStorageService.get(AuthService.TOKEN_KEY)
  }

  public static getRefreshToken(): string {
    return LocalStorageService.get(AuthService.REFRESH_TOKEN_KEY)
  }

  public static setToken(token: string) {
    LocalStorageService.set(AuthService.TOKEN_KEY, token)
  }
  public static setRefreshToken(token: string) {
    LocalStorageService.set(AuthService.REFRESH_TOKEN_KEY, token)
  }
  public static deleteToken() {
    LocalStorageService.remove(AuthService.TOKEN_KEY)
    LocalStorageService.remove(AuthService.REFRESH_TOKEN_KEY)
  }

  clearLocalStorageAndRedirectToLogin() {
    AuthService.deleteToken()
    this.cs.routeWithPathParams(GlobalConstants.ROUTING.LOGIN, [LocalStorageService.get('companyDomain')])
  }

  redirectToLandingPage() {
    this.cs.routeTo(GlobalConstants.ROUTING.LANDING)
  }

  isUserAuthenticated(): boolean {
    const token = AuthService.getToken()
    if (!token) {
      return false
    }
    return true
  }

  async getLoggedInUser() {
    this.appState.select(getUserFromState).subscribe(user => {
      return user
    })
  }

  dispatchSelectClientAction(groupId: string, entityIds: string[], callback: Function) {
    this.appState.dispatch(start_select_client({ groupId, entityIds, callback }))
  }

  callSelectClientAPI(groupId: string, entityIds: string[]): Observable<TokenAPIModel> {
    let headers = new HttpHeaders()
    headers = this.webService.addHeader(headers, ServiceUrlConstants.HEADER_X_COMPANY_ENTITY_IDS, entityIds.join(','))
    headers = this.webService.addHeader(headers, ServiceUrlConstants.HEADER_X_COMPANY_GROUP_ID, groupId)
    return this.webService
      .put(ServiceUrlConstants.API_SELECT_CLIENT, null, headers, [])
      .pipe(map((data: TokenAPIModel) => data))
  }

  dispatchSaveSelectedCompanies(selectedCompanies: SelectedCompanyAPIModel[]) {
    this.initializeService.dispatchSaveSelectedCompanies(selectedCompanies)
  }

  dispatchForgotPasswordAction(email: string, callback: Function) {
    return this.appState.dispatch(start_forgot_password({ email, callback }))
  }

  callForgotPasswordAPI(email: string) {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_FORGOT_PASSWORD, email, null, [], false, true)
  }

  dispatchResetPasswordWithOldPasswordAction(oldPassword: string, newPassword: string, callback: Function) {
    return this.appState.dispatch(start_reset_password_with_password({ oldPassword, newPassword, callback }))
  }

  callResetPasswordWithOldPasswordAPI(oldPassword: string, newPassword: string) {
    return this.webService.post(ServiceUrlConstants.API_RESET_WITH_PASSWORD, { oldPassword, newPassword }, null, [])
  }

  ssoLogin(id_token: string, companyDomain: string): Observable<Login> {
    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_SSO_LOGIN, { msalToken: id_token, companyDomain }, null, [], false, false)
      .pipe(map((data: Login) => data))
  }

  sendLoginOTP(email: string): Observable<void> {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_GENERATE_OTP_FOR_LOGIN, { email }).pipe(
      map(() => void 0),
      catchError((err: HttpErrorResponseModel) => {
        const message = this.cs.extractApiErrorMessage(err) ?? 'Something went wrong. Please try again later.'

        this.cs.showError(message)
        return EMPTY
      })
    )
  }

  loginUsingOTP(email: string, otp: string) {
    const formData = new FormData()
    formData.append('email', email)
    formData.append('otp', otp)

    return this.webService
      .postWithoutAuth(ServiceUrlConstants.API_OTP_BASED_LOGIN, formData, null, [], true)
      .pipe(map((data: Login) => data))
  }

  discoverLoginOptions(companyDomain: string): Observable<LoginDiscoveryResponseApiModel> {
    return this.webService.postWithoutAuth(ServiceUrlConstants.API_AUTH_LOGIN_FOR_DISCOVERY, { companyDomain }).pipe(
      catchError((err: HttpErrorResponseModel) => {
        const message = this.cs.extractApiErrorMessage(err) ?? 'Something went wrong. Please try again later.'

        this.cs.showError(message)
        return EMPTY
      })
    )
  }
}
